'use client';

import { Box, Link, Typography } from '@coolblue-development/freddy';
import selectAvailableStoresPickupTrigger from '@coolblue-development/ga4-events/triggers/OrderBlock/selectAvailableStoresPickupTrigger';
import orderBlockSummaryPickUpInStoreTrigger from '@coolblue-development/ga4-events/triggers/OrderBlockSummary/pickUpInStoreTrigger';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { AvailabilityState, type Product } from '@features/product/typings';
import { useTranslation } from '@i18n/useTranslation';
import { useState } from 'react';

import StoreStockModal from '../StoreStockModal/StoreStockModal';

type StorePickupPropositionProps = {
  product: Product;
  dateOverride?: string;
  isOrderSummary?: boolean;
  isIphonePreOrder: boolean;
};

export function StorePickupProposition({
  product,
  dateOverride,
  isOrderSummary,
  isIphonePreOrder
}: StorePickupPropositionProps) {
  const [open, setOpen] = useState(false);
  const { trackEvent } = useAnalytics();
  const { t, Trans } = useTranslation('webshop.productPage.storePickup.propositions');
  const availableForPickupInStore =
    product?.storeAvailabilities?.filter(
      item =>
        item.availabilityState === AvailabilityState.OnStock ||
        item.availabilityState === AvailabilityState.PreOrder
    ) || [];

  const getTranslationKey = (availabilityStatus: AvailabilityState) => {
    switch (availabilityStatus) {
      case AvailabilityState.OnStock:
        return 'onStock';
      case AvailabilityState.PreOrder:
        return isIphonePreOrder ? 'pickItUp' : 'preOrder';
      default:
        return 'default';
    }
  };

  const handleLinkClick = () => {
    setOpen(true);
    trackEvent(
      isOrderSummary
        ? orderBlockSummaryPickUpInStoreTrigger({
            sub_feature: 'pick_up_locations'
          })
        : selectAvailableStoresPickupTrigger()
    );
  };

  return (
    availableForPickupInStore?.length > 0 && (
      <Box>
        <Typography>
          <Trans
            t={t}
            i18nKey={getTranslationKey(product.availabilityState)}
            components={{
              linkComponent: <Link onClick={handleLinkClick} />
            }}
            count={availableForPickupInStore?.length}
          />
        </Typography>
        <StoreStockModal
          open={open}
          onClose={() => setOpen(false)}
          product={product}
          dateOverride={dateOverride}
          {...(product.personalPrice && { personalPrice: product.personalPrice })}
        />
      </Box>
    )
  );
}
